import { yupResolver } from "@hookform/resolvers/yup";
import {
  ChevronLeftIcon,
  EnterIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Select,
  Spinner,
  Tabs,
  Text,
  TextField,
} from "@radix-ui/themes";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CheckboxComponent from "../../components/inputs/Checkbox/CheckboxComponent";
import CheckboxGroupComponent from "../../components/inputs/CheckboxGroup/CheckboxGroupComponent";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import SelectSearchableComponent from "../../components/inputs/Select/SelectSearchableComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useExportDocument } from "../../hooks/useExportDocument";
import { useGetCoursePlansQuery } from "../../hooks/useGetCoursePlansQuery";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetEnrollmentRequiredDocumentsQuery } from "../../hooks/useGetEnrollmentRequiredDocumentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { useGetSchoolBankConfigQuery } from "../../hooks/useGetSchoolBankConfigQuery";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import { useIES } from "../../hooks/useIES";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { setReferencesData } from "../../redux/slices/finance/reference";
import { resetLoaderAvailable, setLoaderAvailable } from "../../redux/slices/loaderController";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import {
  createPaymentReference,
  getReferenceCodeV2,
} from "../../services/finance/paymentReference";
import { createPaymentTransaction } from "../../services/finance/paymentTransaction";
import {
  createStudentDebt,
  createStudentDebtReferences,
} from "../../services/finance/studentDebt";
import { getPlanSubjects } from "../../services/school/PlanSubjects";
import { createEnrollment } from "../../services/school/student/createEnrollment";
import { createEnrollmentDocument } from "../../services/school/student/createEnrollmentDocument";
import {
  createStudent,
  createStudentCode,
} from "../../services/school/student/createStudent";
import {
  createSubscription,
  createSubscriptionSubject,
} from "../../services/school/student/createSubscription";
import { getEnrollmentsDocuments } from "../../services/school/student/getEnrollmentsDocuments";
import { getSubjectsReprovedQuery } from "../../services/school/subject/getSubjects";
import { addUserRole } from "../../services/user/addUserRole";
import { createUser } from "../../services/user/createUser";
import { staticRoles } from "../../staticRoles";
import styles from "../../styles/CreateUser.module.scss";
import { EnrollmentRequiredDocumentType } from "../../types/EnrollmentRequiredDocument";
import {
  CreateStudentDebtReferenceType,
  PaymentReferenceType,
  PaymentTransactionType,
} from "../../types/Finance";
import {
  ActiveSegmentTab,
  CreateStudentType,
  StudentType,
  SubscriptionType
} from "../../types/Student";
import {
  Countries,
  Country,
  isLicenciature,
  provinces,
  Regimes,
  StudentEletronicDevice,
  StudentSpecialConditions
} from "../../utils/codes";
import { addDaysToDate, formatDateForInput } from "../../utils/dateFormatter";
import { getFile } from "../../utils/fileUtils";
import { stringToBoolean } from "../../utils/utilities";
import { CreateEnrollmentFormValidation } from "./CreateEnrollmentFormValidation";
import { StudentPhotoUpload } from "./StudentPhotoUpload";

export const CreateEnrollment = () => {
  const [currentPage, setCurrentPage] = useState(1);
  useState<CreateStudentDebtReferenceType | null>(null);

  const dispatch = useAppDispatch();
  const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>("user");
  const { exportDocumentPdf: exportDocument, downloadOnly } = useExportDocument();
  const userData = useSelector((state: RootState) => state.login.userData);
  const loginData = useSelector((state: RootState) => state.login.userData);
  const { logoBase64 } = useLogoBase64();
  const { IES } = useIES();

  const handlePrintSubscription = async (s: SubscriptionType, subjects: any[]) => {
    try {
      dispatch(setLoaderAvailable(true));
      const data: any = {
        type: "SUBSCRIPTION",
        outputFormat: "DOCX",
        data: {
          ies: IES.NAME ?? "",
          ano_lectivo: s.academicYear ?? "",
          nome_completo: s.student ?? "",
          codigo: s.studentCode,
          curso: s.course,
          data_emissao: new Date().toLocaleDateString(),
          utilizador: userData?.Fullname ?? "",
          cargo: loginData?.Role ?? "",
          grades: subjects.map((sub, index) => ({
            ordem: index + 1,
            nome_disciplina: sub.subject,
            nivel: s.level,
            semestre: s.semester
          })),
        },
        logoBase64: logoBase64 ?? ""
      };

      const blob = await exportDocument(data, "SUBSCRIPTION");
      toast.success("Documento de inscrição exportado com sucesso!");
    } catch (err) {
      console.error("Erro ao imprimir inscrição:", err);
      toast.error("Erro ao exportar o documento de inscrição.");
    } finally {
      dispatch(resetLoaderAvailable());
    }
  };
  const handlePrintStudentProcess = async (
    enrollmentForm: CreateStudentType,
    isPaymentByEntityReference: boolean = false,
    amountToPay: number = 0,
    payments: any[] = []
  ) => {
    dispatch(setLoaderAvailable(true));

    const data: any = {

      type: "STUDENT_PROCESS",
      outputFormat: "DOCX",
      data: {
        ies: IES.NAME,
        ano_lectivo: enrollmentForm.enrollment.academicYear,
        nome_completo: enrollmentForm.student?.fullname,
        codigo: enrollmentForm.student?.schoolCode,
        curso: enrollmentForm.enrollment.course,
        data_emissao: new Date().toLocaleDateString(),
        cargo: userData?.Role,
        utilizador: userData?.Fullname ?? "",
        delegacao: IES.ENROLLMENT_DEPARTAMENT_NAME
      },
      logoBase64: logoBase64 ?? '',
    };
    if (isPaymentByEntityReference) {
      data.type = "STUDENT_PROCESS_REFERENCE";
      data.data.valor = enrollmentForm.payment?.totalPrice ?? 0;
      data.data.referencia = enrollmentForm.payment?.reference ?? "";
      data.data.banco = enrollmentForm.payment?.bankName ?? "";
      data.data.entidade = enrollmentForm.payment?.entity ?? "";
    }

    await exportDocument(data, "STUDENT_PROCESS");
    dispatch(resetLoaderAvailable());
    toast.success("Processo do estudante exportado com sucesso!");
  }


  const mutationCreateUser = useMutation({
    mutationFn: createUser,
    mutationKey: "createUser",
  });
  const mutationCreateStudentCode = useMutation({
    mutationFn: createStudentCode,
    mutationKey: "createStudentCode",
  });
  const mutationCreateStudent = useMutation({
    mutationFn: createStudent,
    mutationKey: "createStudent",
  });
  const mutationCreateEnrollment = useMutation({
    mutationFn: createEnrollment,
    mutationKey: "createEnrollment",
  });
  const mutationCreateEnrollmentDocument = useMutation({
    mutationFn: createEnrollmentDocument,
    mutationKey: "createEnrollmentDocument",
  });
  const mutationCreateSubscription = useMutation({
    mutationFn: createSubscription,
    mutationKey: "createSubscription",
  });
  const mutationCreateSubscriptionSubject = useMutation({
    mutationFn: createSubscriptionSubject,
    mutationKey: "createSubscriptionSubject",
  });
  const mutationAddUserRole = useMutation({
    mutationKey: "addUserRole",
    mutationFn: addUserRole,
  });
  const mutationSaveReferenceCode = useMutation({
    mutationFn: createPaymentReference,
    mutationKey: "createPaymentReference",
  });
  const mutationCreatePaymentTransaction = useMutation({
    mutationFn: createPaymentTransaction,
    mutationKey: "createPaymentTransaction",
  });

  const {
    data: schoolBankConfigsData,
    status: schoolBankConfigsStatus,
    isError: isErrorSchoolschoolBankConfigs,
    isLoading: isLoadingSchoolschoolBankConfigs,
  } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const {
    data: paymentTypesData,
    status: paymentTypesStatus,
    isError: isErrorPaymentTypes,
    isLoading: isLoadingPaymentTypes,
  } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });

  const mutationCreateStudentDebt = useMutation({
    mutationFn: createStudentDebt,
    mutationKey: "createStudentDebt",
  });

  const navigate = useNavigate();
  const { data: documentsData } = useGetEnrollmentRequiredDocumentsQuery({
    PageNumber: 1,
    PageSize: 400,
  });
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const loadingState =
    mutationCreateUser.isLoading ||
    mutationCreateStudentCode.isLoading ||
    mutationCreateStudent.isLoading ||
    mutationCreateEnrollment.isLoading ||
    mutationCreateEnrollmentDocument.isLoading ||
    mutationCreateSubscription.isLoading ||
    mutationCreateSubscriptionSubject.isLoading ||
    mutationAddUserRole.isLoading ||
    mutationCreateStudentDebt.isLoading ||
    mutationSaveReferenceCode.isLoading ||
    mutationCreatePaymentTransaction.isLoading;
  const defaultValues: CreateStudentType = {
    user: {
      name: "",
      email: "",
      phoneNumber: "",
      gender: "M",
      bi: "",
      userName: "",
      nuit: "",
      dateOfBirth: "",
      isForeign: false,
    },
    documents: [],
    enrollment: {
      idCourse: "",
      date: new Date().toLocaleDateString(),
      idAcademicYear: currentYear?.id ?? "",
      regime: 1,
      subscribeStudent: true,
    },
    student: {
      specialConditions: "",
      eletronicDevice: "",
      enrollmentYear: new Date().getFullYear(),
      province: "",
      provinceGraduated12OrEqual: "",
      localGraduated12OrEqual: "",
      idUser: "",
      studentUniversalCode: "",
      photoFile: null,
      photoPreview: "",
      photoUploaded: false,
    },
    idFaculty: "",
    idDepartament: "",
    idPlan: "",
    enrollment_date: new Date().toString(),
    subscribeStudent: true,
    schoolCode: undefined,
    isInternStudent: false,
    year: 1,
    semester: 1,
    // isForeign: false,
    countryDescription: "",
    retrySubjectsReproved: [],
    subjectsSubscriptions: [],
    payment: {
      isPaymentByEntityReference: true,
      paymentProof: "",
      idBankEntity: "",
    },
  };
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
    reset,
    resetField,
    clearErrors
  } = useForm<CreateStudentType>({
    resolver: yupResolver(CreateEnrollmentFormValidation),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
    shouldUnregister: false,
  });

  const watchIsInternStudent = watch("isInternStudent");
  const {
    data: universities,
    isLoading: isLoadingUniversities,
    isError: errorUniversities,
    refetch: refetchUniversities,
  } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const {
    data: faculties,
    isLoading: isLoadingFaculties,
    isError: errorFaculties,
    refetch: refetchFaculties,
  } = useGetFacultiesQuery({
    PageNumber: 1,
    PageSize: 400,
    IdUniversity: getValues("idUniversity"),
  },
    { enabled: !!getValues("idUniversity") }
  );
  const {
    data: departaments,
    isLoading: isLoadingDepartaments,
    isError: errorDepartaments,
    refetch: refreshDepartaments,
  } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues("idFaculty") },
    { enabled: !!getValues("idFaculty") }
  );

  const {
    data: courses,
    isLoading: isLoadingCourses,
    isError: errorCourses,
    refetch: refreshCourses,
  } = useGetCoursesQuery(
    { PageNumber: 1, PageSize: 400, IdDepartament: getValues("idDepartament") },
    { enabled: !!getValues("idDepartament") }
  );

  const {
    data: coursePlans,
    isLoading: isLoadingCoursePlans,
    isError: errorCoursePlans,
    refetch: refreshCoursePlans,
  } = useGetCoursePlansQuery(
    {
      PageNumber: currentPage,
      PageSize: 400,
      IdCourse: getValues("enrollment.idCourse"),
    },
    { enabled: !!getValues("enrollment.idCourse") }
  );

  const {
    data: planSubjectsData,
    isLoading: isLoadingPlanSubjectsData,
    isError: isErrorPlanSubjectsData,
    refetch: refreshPlanSubjects,
    status: planSubjectsStatus,
  } = useQuery(
    ["getPlanSubjectsQuery"],
    async () => {
      try {
        const data = await getPlanSubjects({
          PageSize: 400,
          PageNumber: 1,
          Level: getValues("year") === 1 ? 1 : undefined,
          Semester: getValues("semester"),
          IdPlan: getValues("idPlan"),
          IdCourse: getValues("enrollment.idCourse"),
        });
        const responseData = { ...data };
        responseData.items = responseData.items.filter(
          (i) => i.level <= getValues("year")!
        );
        return responseData;
      } catch { }
    },
    {
      enabled: !!getValues("idPlan"),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const navigateAndValidate = async (tabToNavigateTo: ActiveSegmentTab) => {
    if (activeSegment === tabToNavigateTo) return;

    if (tabToNavigateTo === "course") {
      const isValid = await trigger('user');
      if (!isValid) return;
    }

    if (tabToNavigateTo === "manage-subjects") {
      const isValid = await trigger(['idUniversity', 'idFaculty', 'idDepartament', 'enrollment', 'idPlan']);
      if (!isValid) return;

      fetchReprovedSubjects();
      refreshPlanSubjects();
    }

    if (tabToNavigateTo === "payments") {
      const isValid = await trigger(['retrySubjectsReproved', 'subjectsSubscriptions']);
      if (!isValid) return;
    }

    if (tabToNavigateTo === "documents") {
      const isValid = await trigger('payment');
      if (!isValid) return;
    }

    setActiveSegment(tabToNavigateTo);
  };


  const courseSelected = courses?.items.find(c => c.id === watch("enrollment.idCourse"));
  const provinceEnrolledCompletedLevel = isLicenciature(courseSelected?.level ?? "") ? "Provincia completou á 12.ª Classe ou equivalente " : "Provincia do nivel concluído";

  const onSubmit = handleSubmit(async (data) => {
    try {
      let _uniqueSubjects: any[] = [];

      try {
        if (
          isEmpty(data.user.userName) ||
          data.user.userName?.length === 1 ||
          data.user.userName === "1"
        ) {
          data.user.userName = data.user.bi;
        }
      } catch (ex) { }

      const resultCreateUser = await mutationCreateUser.mutateAsync(
        data.user as any
      );
      if (!resultCreateUser) return;
      const { id: idUser } = resultCreateUser as any;
      const resultAddUserRole = await mutationAddUserRole.mutateAsync({
        roleId: staticRoles.Student,
        userId: idUser,
      });
      if (!resultAddUserRole) return;
      let studentCode = data.schoolCode;
      var { name, code, description, level, modality } =
        courses?.items.find((i) => i.id === data.enrollment.idCourse) ?? {};
      let syncAndGenerateCode = {
        bi: data.user.bi,
        idCourse: data.enrollment.idCourse,
        document: data.user?.bi,
        provinceEnrolled:
          departaments?.items.find((i) => i.id === getValues("idDepartament"))
            ?.province ??
          faculties?.items.find((i) => i.id === getValues("idFaculty"))
            ?.idProvince ??
          universities?.items.find((i) => i.id === getValues("idUniversity"))
            ?.province,
        isNewStudent: data.student?.enrollmentYear === currentYear?.year,
        regime: data.enrollment.regime,
        idRegime: data.enrollment.regime,
        enrollmentYear: `${data.student?.enrollmentYear}`,
        code: code,
        courseName: name ?? description,
        nuit: stringToBoolean(data.user.isForeign)
          ? data.user.bi
          : data.user.nuit,
        year: currentYear?.year,
        idUser: idUser,
        name: data.user.name,
        gender: `${data.user.gender}`,
        dateOfBirth: data.user.dateOfBirth,
        isForeign: stringToBoolean(data.user.isForeign),
        country: data.countryDescription ?? "MOZ",
        specialConditions: data.student?.specialConditions,
        eletronicDevice: data.student?.eletronicDevice,
        province: data.student?.province,
        provinceGraduated12OrEqual: data.student?.provinceGraduated12OrEqual,
        localGraduated12OrEqual: data.student?.localGraduated12OrEqual,
        courseLevel: level,
        modality: modality,
      };
      if (isEmpty(studentCode)) {
        const resultCreateStudentCode =
          await mutationCreateStudentCode.mutateAsync(syncAndGenerateCode);
        studentCode = resultCreateStudentCode;
      }
      if (!studentCode) return;
      let studentPhotoBase64: string | undefined = undefined;

      if (data.student?.photoFile) {
        const { base64 } = await getFile(
          data.student.photoFile,
          "createStudent"
        );
        studentPhotoBase64 = base64 as string | undefined;
      }
      const timestamp = new Date().getTime();
      const resultCreateStudent = await mutationCreateStudent.mutateAsync({
        idUser: idUser,
        studentUniversalCode: studentCode + "-" + timestamp,
        schoolCode: studentCode,
        fullname: data.user.name,
        isForeign: stringToBoolean(data.user.isForeign),
        countryDescription: data.countryDescription ?? "MOZ",
        specialConditions: data.student?.specialConditions,
        eletronicDevice: data.student?.eletronicDevice,
        province: data.student?.province,
        provinceGraduated12OrEqual: data.student?.provinceGraduated12OrEqual,
        localGraduated12OrEqual: data.student?.localGraduated12OrEqual,
        father: data.student?.father,
        mother: data.student?.mother,
        dateDocumentEmmited: data.student?.dateDocumentEmmited,
        enrollmentYear: data.student?.enrollmentYear,
        photo: studentPhotoBase64,
      });
      if (!resultCreateStudent) return;
      Object.assign(syncAndGenerateCode, {
        ...syncAndGenerateCode,
        idStudent: resultCreateStudent,
      });
      const resultCreateEnrollment = await mutationCreateEnrollment.mutateAsync(
        {
          idCourse: data.enrollment.idCourse,
          idAcademicYear: currentYear?.id,
          idStudent: resultCreateStudent,
          regime: data.enrollment.regime,
          subscribeStudent: Boolean(data.enrollment.subscribeStudent),
          idPlan: data.idPlan,
          syncData: syncAndGenerateCode,
        }
      );
      if (!resultCreateEnrollment) return;

      try {
        const convertedToBase64Docs = await Promise.all(
          data.documents?.map(async (document) => {
            return getFile(document.file, document.id);
          })
        );
        const saveDocuments = await Promise.all(
          convertedToBase64Docs.map((cv) => {
            return mutationCreateEnrollmentDocument.mutateAsync({
              docBase64: cv.base64,
              idEnrollment: resultCreateEnrollment,
              idRequiredDocument: cv.id,
              name: cv.name,
              extension: cv.extension,
            });
          })
        );
      } catch (ex: any) {
        toast.error(ex?.message);
      }
      let msg = "Estudante matriculado";
      const _subscribeStudent = data?.enrollment?.subscribeStudent;
      let referenceCode;
      let studentDebtData;
      let idSubscription;
      if (stringToBoolean(_subscribeStudent)) {
        const resultCreateSubscription =
          await mutationCreateSubscription.mutateAsync({
            idEnrollment: resultCreateEnrollment,
            semester: data?.semester ?? 1,
            year: data?.year ?? 1,
            level: data?.year ?? 1,
            idAcademicYear: currentYear?.id,
          });
        if (!resultCreateSubscription) return;
        idSubscription = resultCreateSubscription;
        msg += " e inscrito";
        try {
          const allSubjects: any[] = [];
          if (
            data.retrySubjectsReproved &&
            data.retrySubjectsReproved.length > 0
          ) {
            data.retrySubjectsReproved.forEach((s: any) => {
              s = JSON.parse(s);
              if (s.semester === data.semester) {
                allSubjects.push({
                  idSubject: s.idSubject,
                  idSubscription: resultCreateSubscription,
                  status: true,
                  level: s?.level,
                  semester: s?.semester,
                  subject: s?.subject,
                  isMonograph: s?.isMonograph,
                });
              }
            });
          }
          if (
            data.subjectsSubscriptions &&
            data.subjectsSubscriptions.length > 0
          ) {
            data.subjectsSubscriptions.forEach((s: any) => {
              s = JSON.parse(s);
              const precedence = reprovedSubjectsData?.items.find(
                (rs) => rs.idSubject === s.idSubjectPrecedence
              );
              if (!(precedence && precedence?.id)) {
                if (s.semester === data.semester) {
                  allSubjects.push({
                    idSubject: s.idSubject,
                    idSubscription: resultCreateSubscription,
                    status: true,
                    level: s?.level,
                    semester: s?.semester,
                    subject: s?.subject,
                    isMonograph: s?.isMonograph,
                  });
                }
              }
            });
          }
          const uniqueSubjects = Array.from(
            new Set(allSubjects.map((subject) => subject.idSubject))
          ).map((idSubject) =>
            allSubjects.find((subject) => subject.idSubject === idSubject)
          );

          if (uniqueSubjects.length > 0) {
            await Promise.all(
              uniqueSubjects.map((subject) => {
                mutationCreateSubscriptionSubject.mutateAsync(subject);
                _uniqueSubjects.push(subject);
              })
            );
          }
        } catch (error) {
          console.error({ error });
        }

        // START HANDLE PRINT FILES
        const s: SubscriptionType = {
          academicYear: currentYear?.year,
          course: courses?.items.find(
            (course) => course.id === data.enrollment.idCourse
          )?.description,
          planSubjects: _uniqueSubjects,
          id: resultCreateSubscription,
          idAcademicYear: currentYear?.id,
          idCourse: data.enrollment.idCourse,
          idEnrollment: resultCreateEnrollment,
          level: data?.year ?? 1,
          reason: "",
          semester: data?.semester ?? 1,
          status: true,
          student: data.user.name,
          studentCode: studentCode,
        } as any;

        // END HANDLE PRINT FILES
      }
      try {

        let amountToPay =
          paymentTypesData?.items.find(
            (paymentType) =>
              paymentType.id === data?.payment?.idPaymentTypeEnrollment!
          )?.amount ?? 0;
        const isSubscription = stringToBoolean(
          getValues("enrollment.subscribeStudent")
        );
        if (isSubscription) {
          const subscriptionAmount =
            paymentTypesData?.items.find(
              (paymentType) =>
                paymentType.id === data?.payment?.idPaymentTypeSubscription!
            )?.amount ?? 0;
          amountToPay += subscriptionAmount;

          const subjectsAmount =
            paymentTypesData?.items.find(
              (paymentType) =>
                paymentType.id === data?.payment?.idPaymentTypePerSubject!
            )?.amount ?? 0;
          const subjectsTotal =
            _uniqueSubjects.filter(
              (ss: any) => !stringToBoolean(ss?.isMonograph)
            ).length * subjectsAmount;
          amountToPay += subjectsTotal;

          const subjectsMonographAmount =
            paymentTypesData?.items.find(
              (paymentType) =>
                paymentType.id === data.payment?.idPaymentTypeMonograph
            )?.amount ?? 0;
          const subjectMonograph =
            (_uniqueSubjects?.filter((ss: any) =>
              stringToBoolean(ss?.isMonograph)
            ).length ?? 0) * subjectsMonographAmount;

          amountToPay += subjectMonograph;
        }
        studentDebtData = {
          idStudent: resultCreateStudent,
          fullname: data.user.name.concat(" ").concat(studentCode) ?? "",
          idPaymentType: data.payment?.idPaymentTypeEnrollment!,
          originalAmount: amountToPay,
          currentAmount: amountToPay,
          dueDate: formatDateForInput(
            addDaysToDate(new Date(), 30).toISOString()
          ),
          isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference)
            ? false
            : true,
          description:
            paymentTypesData?.items.find(
              (paymentType) =>
                paymentType.id === data?.payment?.idPaymentTypeEnrollment!
            )?.description ?? "N/D",
          idEnrollment: resultCreateEnrollment,
          idSubscription: idSubscription,
          idsSubjects: JSON.stringify(
            _uniqueSubjects.map((s: any) => s.idSubject)
          ),
        };
        const resultCreateStudentDebt =
          await mutationCreateStudentDebt.mutateAsync(studentDebtData);
        studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };
        if (amountToPay > 0) {
          if (
            stringToBoolean(getValues("payment.isPaymentByEntityReference"))
          ) {
            const currentDate = new Date();
            try {
              referenceCode = await getReferenceCodeV2({
                amount: studentDebtData.currentAmount,
                idStudent: studentDebtData.idStudent,
                idPaymentType: studentDebtData.idPaymentType,
                idStudentDebt: studentDebtData.id,
                month: (currentDate.getMonth() + 1).toString(),
                year: currentDate.getFullYear().toString(),
                description: studentDebtData.description,
                idBankEntity: data?.payment?.idBankEntity,
                entity: String(
                  schoolBankConfigsData?.items.find(
                    (i) => i.id === getValues("payment.idBankEntity")
                  )?.bankEntity
                ),
              });
              const paymentReference: PaymentReferenceType = {
                idStudent: studentDebtData.idStudent,
                idPaymentType: studentDebtData.idPaymentType,
                amount: studentDebtData.currentAmount,
                dueDate: new Date(studentDebtData.dueDate).toISOString(),
                referenceCode: referenceCode,
                idStudentDebt: studentDebtData.id,
                entity: String(
                  schoolBankConfigsData?.items.find(
                    (i) => i.id === getValues("payment.idBankEntity")
                  )?.bankEntity
                ),
                idBankEntity: data?.payment?.idBankEntity,
              };
              await mutationSaveReferenceCode.mutateAsync(paymentReference);
            } catch (ex: any) {
              toast.error(ex?.message);
            }
          } else {
            const { base64 } = await getFile(data.payment?.paymentProof);
            const paymentTransaction: PaymentTransactionType = {
              date: new Date().toISOString(),
              idPaymentType: data.payment?.idPaymentTypeEnrollment!,
              amountPaid: isSubscription
                ? amountToPay
                : paymentTypesData?.items.find(
                  (paymentType) =>
                    paymentType.id === data?.payment?.idPaymentTypeEnrollment!
                )?.amount ?? 0,
              idStudent: resultCreateStudent,
              paymentProof: base64,
              idStudentDebt: resultCreateStudentDebt,
              idCompleted: true,
            };
            await mutationCreatePaymentTransaction.mutateAsync(
              paymentTransaction
            );
          }
        }

      } catch (error: any) {
        toast.error(error?.message);
      }
      const userData = { ...data.user, id: idUser };
      const studentData: StudentType = {
        idUser: idUser,
        studentUniversalCode: studentCode,
        schoolCode: studentCode,
      };
      const { items } = await getEnrollmentsDocuments({
        PageNumber: 1,
        PageSize: 100,
        IdEnrollment: resultCreateEnrollment,
      });

      const schoolBankConfig = schoolBankConfigsData?.items.find(
        (i) => i.id === getValues("payment.idBankEntity")
      );

      const enrollmentForm: CreateStudentType = {
        user: {
          ...userData,
          isForeign: stringToBoolean(studentData.isForeign),
        },
        documents: [],
        enrollment: {
          id: data?.enrollment?.id,
          idStudent: data?.enrollment?.idStudent,
          idCourse: data?.enrollment?.idCourse,
          date: data?.enrollment?.date,
          idAcademicYear: data?.enrollment?.idAcademicYear,
          academicYear: currentYear?.year,
          status: data?.enrollment?.status,
          course: courses?.items.find(
            (course) => course.id === data.enrollment.idCourse
          )?.description,
          departament: departaments?.items.find(
            (departament) => departament.id === data?.idDepartament
          )?.description,
          faculty: faculties?.items.find(
            (faculty) => faculty.id === data?.idFaculty
          )?.description,
          regime: data?.enrollment.regime,
          subscribeStudent: data?.enrollment.subscribeStudent ?? false,
        },
        idFaculty: data?.idFaculty ?? "",
        idDepartament: data?.idDepartament ?? "",
        idPlan: data?.idPlan ?? "",
        storeDocuments: items,
        subscribeStudent: data?.enrollment?.subscribeStudent ?? false,
        isInternStudent: true,
        student: {
          ...studentData,
          countryDescription: studentData.countryDescription ?? "",
          fullname: data?.user?.name,
        },
        countryDescription: studentData.countryDescription ?? "",
        payment: {
          reference: referenceCode,
          paymentProof: data?.payment?.paymentProof ?? "",
          bankName: schoolBankConfig?.bankName,
          bankDescription: schoolBankConfig?.bankDescription,
          entity: String(schoolBankConfig?.bankEntity),
          totalPrice: studentDebtData?.currentAmount,
        },
      };


      if (stringToBoolean(_subscribeStudent)) {
        const s: SubscriptionType = {
          year: data?.year ?? 1,
          academicYear: currentYear?.year,
          course: courses?.items.find(
            (course) => course.id === data.enrollment.idCourse
          )?.description,
          planSubjects: _uniqueSubjects,
          idAcademicYear: currentYear?.id,
          idCourse: data.enrollment.idCourse,
          idEnrollment: resultCreateEnrollment,
          level: data?.year ?? 1,
          reason: "",
          semester: data?.semester ?? 1,
          status: true,
          student: data.user.name,
          studentCode: studentCode,
        }
        await handlePrintSubscription(s, _uniqueSubjects);
      }
      await handlePrintStudentProcess(
        enrollmentForm,
        stringToBoolean(data.payment?.isPaymentByEntityReference),
        amountToPay
      );

      toast.success(msg);
      try {
        if (stringToBoolean(data.isGenerateMonthly)) {
          const createSubscriptionDebtReference: CreateStudentDebtReferenceType =
          {
            studentId: resultCreateStudent,
            studentCode: studentCode,
            fullname: data.user.name,
            enrollmentId: resultCreateEnrollment,
            subscriptionId: idSubscription ?? "",
            paymentTypeId: `${data.paymentTypeId}`,
            idSchoolBankConfig: `${data.bankEntity}`,
            bankEntity: `${schoolBankConfigsData?.items.find(
              (i) => i.id === data.bankEntity
            )?.bankEntity ?? ""
              }`,
            semester: data.semester ?? 1,
            year:
              Number(currentYear?.year) ?? Number(new Date().getFullYear()),
          };
          const references = await createStudentDebtReferences({
            ...createSubscriptionDebtReference,
          });
          const pdfData = { references: references, data: createSubscriptionDebtReference };
          dispatch(setReferencesData(pdfData));
          dispatch(setPdfPreviewType('REFERENCES'));
          localStorage.setItem("studentDebtReferencesData", JSON.stringify(pdfData));
          localStorage.setItem("pdfPreviewTypeData_REFS", "REFERENCES");
          navigate(routes.pdfPreview);
        }
      } catch (error) {
        console.error({ error });
      }
      handleBack();
    } catch (err: any) {
      toast.error(`${err?.message}`);
    }
  });
  const handleEmailChange = (e: any) => {
    var email = e.target.value;
    setValue("user.email", email);
    if (email) {
      setValue("user.userName", email);
    }
  };
  const handleOnChangeYear = (e: any) => {
    var year = e.target.value;
    setValue("year", year);
    resetField("payment.idPaymentTypeMonograph");
    resetField("payment.idPaymentTypeEnrollment");
    resetField("payment.idPaymentTypeSubscription");
    resetField("payment.idPaymentTypePerSubject");
    resetField("retrySubjectsReproved");
    resetField("subjectsSubscriptions");
  };
  const handleBIChange = (e: any) => {
    var bi = e.target.value;
    setValue("user.bi", bi);
    const username = getValues("user.userName");
    if (isEmpty(username) || username?.length === 1 || username === "1") {
      setValue("user.userName", bi);
    }
  };
  const handleBack = () => {
    navigate(routes.enrollments);
  };
  const handleFacultyChange = (idFaculty: string) => {
    setValue("idDepartament", "");
    setValue("enrollment.idCourse", "");
    setValue("idFaculty", idFaculty);
    refreshDepartaments();
  };
  const handleOnChangeIsInternStudent = (e: any) => {
    const isInternStudent = stringToBoolean(getValues("isInternStudent"));
    if (!isInternStudent) {
      resetField("student.schoolCode");
      resetField("schoolCode");
      resetField("year");
      resetField("semester");
      resetField("payment.idPaymentTypePerSubject");
    }
  };
  const watchIsForeign = watch("user.isForeign");
  const watchPaymentType = watch("payment.isPaymentByEntityReference");
  const watchSubscribeStudent = watch("enrollment.subscribeStudent");

  const handleOnChangeIsForeign = (e: any) => {
    const isIsForeign = stringToBoolean(getValues("user.isForeign"));
    if (isIsForeign) {
      resetField("user.nuit");
      setValue("countryDescription", "");
      setValue("student.province", "");
      setValue("student.provinceGraduated12OrEqual", "");
    }
    if (!isIsForeign) {
      setValue("countryDescription", "MOZ");
    }
  };
  const handleOnChangePaymentType = (e: any) => {
    const paymentType = stringToBoolean(
      getValues("payment.isPaymentByEntityReference")
    );
    if (paymentType) {
      resetField("payment.paymentProof");
    }
    if (!paymentType) {
      resetField("payment.idBankEntity");
    }
  };
  const handleOnChangeSubscribe = (e: any) => {
    const subscribeStudent = stringToBoolean(
      getValues("enrollment.subscribeStudent")
    );
    if (!subscribeStudent) {
      resetField("retrySubjectsReproved");
      resetField("subjectsSubscriptions");
    }
  };
  const handleDepartamentChange = (idDepartament: string) => {
    setValue("enrollment.idCourse", "");
    setValue("idPlan", "");

    setValue("idDepartament", idDepartament);
    refreshCourses();
  };

  const handleCourseChange = async (idCourse: string) => {
    setValue("idPlan", "");

    setValue("enrollment.idCourse", idCourse);
    refreshCoursePlans();
  };

  const watchIdPaymentTypePerSubject = watch("payment.idPaymentTypePerSubject");
  const watchIdPaymentTypeMonograph = watch("payment.idPaymentTypeMonograph");
  const watchIdPaymentTypeSubscription = watch(
    "payment.idPaymentTypeSubscription"
  );
  const watchIdPaymentTypeEnrollment = watch("payment.idPaymentTypeEnrollment");
  const watchRetrySubjectsReproved = watch("retrySubjectsReproved");
  const watchSubjectsSubscriptions = watch("subjectsSubscriptions");

  const watchCourse = watch("enrollment.idCourse");
  const course = courses?.items.find((i) => i.id === watchCourse);
  const watchLevelOrYear = watch("year");
  const isLastYearOrLevel =
    Number(course?.duration) === Number(watchLevelOrYear);

  const watchCoursePlan = watch("idPlan");
  const coursePlan = coursePlans?.items.find((i) => i.id === watchCoursePlan);

  let amountToPay =
    paymentTypesData?.items.find(
      (paymentType) => paymentType.id === watchIdPaymentTypeEnrollment
    )?.amount ?? 0;

  const isSubscription = stringToBoolean(watchSubscribeStudent);
  if (isSubscription) {
    const subscriptionAmount =
      paymentTypesData?.items.find(
        (paymentType) => paymentType.id === watchIdPaymentTypeSubscription
      )?.amount ?? 0;
    amountToPay += subscriptionAmount;
    const subjectsAmount =
      paymentTypesData?.items.find(
        (paymentType) => paymentType.id === watchIdPaymentTypePerSubject
      )?.amount ?? 0;
    const subjectsTotal =
      (watchSubjectsSubscriptions
        ?.map((ss) => JSON.parse(ss))
        .filter((ss: any) => !stringToBoolean(ss?.isMonograph)).length ?? 0) *
      subjectsAmount;
    amountToPay += subjectsTotal;
    const subjectsRetryTotal =
      (watchRetrySubjectsReproved
        ?.map((ss) => JSON.parse(ss))
        .filter((ss: any) => !stringToBoolean(ss?.isMonograph)).length ?? 0) *
      subjectsAmount;
    amountToPay += subjectsRetryTotal;

    const subjectsMonographAmount =
      paymentTypesData?.items.find(
        (paymentType) => paymentType.id === watchIdPaymentTypeMonograph
      )?.amount ?? 0;
    const subjectMonograph =
      (watchSubjectsSubscriptions
        ?.map((ss) => JSON.parse(ss))
        .filter((ss: any) => stringToBoolean(ss?.isMonograph)).length ?? 0) *
      subjectsMonographAmount;
    amountToPay += subjectMonograph;
  }
  const paymentTotal = amountToPay;
  const {
    data: reprovedSubjectsData,
    isLoading: isLoadingReprovedSubjects,
    refetch: fetchReprovedSubjects,
    isError: isErrorReprovedSubjects,
    status: reprovedSubjectsStatus,
  } = useQuery(
    ["getSubjectsReprovedQuery", `${getValues("schoolCode")}`],
    async () => {
      try {
        const data = await getSubjectsReprovedQuery({
          PageNumber: 1,
          PageSize: 400,
          StudentCode: `${getValues("schoolCode")}`,
          Year: currentYear?.year,
          Semester: getValues("semester"),
        });
        return data;
      } catch { }
    },
    {
      enabled:
        !!getValues("schoolCode") &&
        !!getValues("semester") &&
        !!getValues("year"),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const handleUniversityChange = (id: string) => {
    setValue("idUniversity", id);
    setValue("idFaculty", "");
    setValue("idDepartament", "");
    refetchFaculties();
  };

  const selectedCourse = courses?.items.find((c) => String(c.id) === watchCourse);
  const watchRegime = watch("enrollment.regime");
  const [documentsDataFiltered, setDocumentsDataFiltered] = useState<EnrollmentRequiredDocumentType[]>([]);

  useEffect(() => {
    if (!documentsData?.items || !selectedCourse?.level || !watchRegime) return;

    const filtered = documentsData.items.filter(
      (doc) => (doc.level == selectedCourse?.level || doc.level == null || doc.level == '') && (doc.regime == watchRegime || doc.regime == null)
    );

    setDocumentsDataFiltered(filtered);

    if (filtered.length === 0) {

      setValue('documents', [] as any);

      if (errors.documents) {
        clearErrors('documents');
      }
    } else {

      const initialDocuments = filtered.map(doc => ({
        id: doc.id,
        file: null as any
      }));
      setValue('documents', initialDocuments as any);
    }
  }, [documentsData?.items, watchCourse, watchRegime, setValue, clearErrors, errors.documents, selectedCourse?.level]);


  if (!currentYear || !currentYear?.status) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Por favor active o ano lectivo
            <Link to={routes.academicYears} style={{ color: "var(--gray-12)" }}>
              Voltar
            </Link>
          </Text>
        </Callout.Root>
      </Flex>
    );
  }
  const isGenerateMonthly = watch("isGenerateMonthly");

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
        <Callout.Root>
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>Valor a pagar : {paymentTotal}</Callout.Text>
        </Callout.Root>
      </Flex>
      <form onSubmit={onSubmit} noValidate>
        <Tabs.Root
          value={activeSegment}
          onValueChange={(tabToNavigate) => {
            navigateAndValidate(tabToNavigate as ActiveSegmentTab);
            if (tabToNavigate.includes("manage-subjects")) {
              fetchReprovedSubjects();
              refreshPlanSubjects();
            }
          }}
        >
          <Tabs.List>
            <Tabs.Trigger value="user">
              Dados gerais{" "}
              {errors?.user && (
                <Badge color="red">{Object.keys(errors?.user)?.length} </Badge>
              )}{" "}
            </Tabs.Trigger>
            <Tabs.Trigger value="course">
              Dados matricula{" "}
              {errors?.enrollment && (
                <Badge color="red">
                  {Object.keys(errors?.enrollment)?.length}{" "}
                </Badge>
              )}{" "}
            </Tabs.Trigger>
            <Tabs.Trigger
              value="manage-subjects"
              disabled={!stringToBoolean(watchSubscribeStudent)}>
              Disciplinas
            </Tabs.Trigger>
            <Tabs.Trigger value="payments">Pagamentos</Tabs.Trigger>
            <Tabs.Trigger value="documents">
              Documentos{" "}

              {documentsDataFiltered.length > 0 && errors?.documents && (
                <Badge color="red">
                  {Object.keys(errors?.documents)?.length}{" "}
                </Badge>
              )}
            </Tabs.Trigger>
          </Tabs.List>
          <Box pt="3">
            <Tabs.Content value="user">
              <Card size="3">
                <Flex direction="column" gap="4">
                  <StudentPhotoUpload
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                  />

                  <Box style={{ width: "100%" }}>
                    <Text as="label" size="2" className={styles.label}>
                      <span>
                        Nome <span style={{ color: "red" }}>*</span>
                      </span>
                    </Text>
                    <Controller
                      name="user.name"
                      control={control}
                      render={({ field }) => (
                        <TextField.Root
                          size="2"
                          {...field}
                          placeholder="Nome completo"
                        />
                      )}
                    />
                    {errors.user?.name && (
                      <Text color="red" size="1">
                        {errors.user?.name.message}
                      </Text>
                    )}
                  </Box>

                  <Flex gap="2">
                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        Email
                      </Text>
                      <Controller
                        name="user.email"
                        control={control}
                        render={({ field }) => (
                          <TextField.Root
                            size="2"
                            {...field}
                            onChange={handleEmailChange}
                            type="email"
                            placeholder="seu@email.com"
                          />
                        )}
                      />
                      {errors.user?.email && (
                        <Text color="red" size="1">
                          {errors.user?.email.message}
                        </Text>
                      )}
                    </Box>
                    <TextFieldComponent
                      name="user.secondaryEmail"
                      label="Email alternativo"
                      control={control}
                      errors={errors}
                      fieldProps={{
                        placeholder: "seu@email.com",
                        type: "email",
                      }}
                      style={{ width: "100%" }}
                    />
                  </Flex>
                  <Flex gap="2">
                    <RadioGroupComponent
                      name="user.isForeign"
                      label="Estrangeiro"
                      control={control}
                      errors={errors}
                      style={{ width: "50%" }}
                      options={[
                        { value: "true", label: "Sim" },
                        { value: "false", label: "Não" },
                      ]}
                      fieldProps={{
                        style: {
                          display: "flex",
                          flexDirection: "row",
                          gap: "16px",
                        },
                        onChange: handleOnChangeIsForeign,
                      }}
                    />
                    <SelectSearchableComponent
                      name="countryDescription"
                      label="País"
                      control={control}
                      errors={errors}
                      options={Countries?.map((country: Country) => ({
                        value: country.code,
                        label: country.label ?? "",
                      }))}
                      style={{ width: "100%" }}
                      fieldProps={{
                        disabled: !stringToBoolean(watchIsForeign),
                      }}
                    />
                    <SelectComponent
                      name="student.province"
                      label="Natural de"
                      control={control}
                      errors={errors}
                      composedName={{ parent: "student", prop: "province" }}
                      options={provinces?.map(
                        (country: { id: any; description: any }) => ({
                          value: country.id,
                          label: country.description ?? "",
                        })
                      )}
                      style={{ width: "100%" }}
                      fieldProps={{ disabled: stringToBoolean(watchIsForeign) }}
                    />
                  </Flex>
                  <Flex gap="2">
                    <TextFieldComponent
                      name="student.father"
                      label="Nome do Pai"
                      control={control}
                      errors={errors}
                      fieldProps={{
                        placeholder: "Nome do Pai",
                        type: "text",
                      }}
                      style={{ width: "100%" }}
                    />
                    <TextFieldComponent
                      name="student.mother"
                      label="Nome da Mãe"
                      control={control}
                      errors={errors}
                      fieldProps={{
                        placeholder: "Nome da Mãe",
                        type: "text",
                      }}
                      style={{ width: "100%" }}
                    />
                  </Flex>
                  <Box style={{ width: "100%" }}>
                    <Text as="label" size="2" className={styles.label}>
                      <span>
                        {stringToBoolean(watchIsForeign)
                          ? "Documento de identificação"
                          : "BI/DIRE/Passaporte/Carta de condução/Etc"}{" "}
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    </Text>
                    <Controller
                      name="user.bi"
                      control={control}
                      render={({ field }) => (
                        <TextField.Root
                          size="2"
                          {...field}
                          placeholder={
                            stringToBoolean(watchIsForeign)
                              ? "Documento de identificação"
                              : "BI/DIRE/Passaporte/Carta de condução"
                          }
                          onChange={handleBIChange}
                        />
                      )}
                    />
                    {errors.user?.bi && (
                      <Text color="red" size="1">
                        {errors.user?.bi.message}
                      </Text>
                    )}
                  </Box>
                  <DateFieldComponent
                    name="student.dateDocumentEmmited"
                    composedName={{
                      parent: "student",
                      prop: "dateDocumentEmmited",
                    }}
                    label="Data de Emissão do Documento"
                    control={control}
                    errors={errors}
                    style={{ width: "100%" }}
                  />

                  <Flex gap="2">

                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        <span> NUIT </span>
                      </Text>
                      <Controller
                        name="user.nuit"
                        control={control}
                        render={({ field }) => (
                          <TextField.Root
                            size="2"
                            {...field}
                            placeholder="Seu NUIT"
                          />
                        )}
                      />
                      {errors.user?.nuit && (
                        <Text color="red" size="1">
                          {errors.user?.nuit.message}
                        </Text>
                      )}
                    </Box>

                    <TextFieldComponent
                      name="user.phoneNumber"
                      composedName={{ parent: "user", prop: "phoneNumber" }}
                      label="Telefone"
                      control={control}
                      errors={errors}
                      fieldProps={{ placeholder: "82xxxxxxx" }}
                      style={{ width: "100%" }}
                      required={true}
                    />

                    <TextFieldComponent
                      name="user.secondaryPhoneNumber"
                      composedName={{
                        parent: "user",
                        prop: "secondaryPhoneNumber",
                      }}
                      label="Telefone"
                      control={control}
                      errors={errors}
                      fieldProps={{ placeholder: "82xxxxxxx" }}
                      style={{ width: "100%" }}
                    />
                  </Flex>
                  <Flex gap="2">
                    <DateFieldComponent
                      name="user.dateOfBirth"
                      composedName={{ parent: "user", prop: "dateOfBirth" }}
                      label="Data de Nascimento"
                      control={control}
                      errors={errors}
                      style={{ width: "48%" }}
                      required={true}
                    />
                    <RadioGroupComponent
                      name="user.gender"
                      label="Gênero"
                      control={control}
                      errors={errors}
                      style={{ width: "100%" }}
                      options={[
                        { value: "M", label: "Masculino" },
                        { value: "F", label: "Feminino" },
                      ]}
                      fieldProps={{
                        style: {
                          display: "flex",
                          flexDirection: "row",
                          gap: "16px",
                        },
                      }}
                    />
                  </Flex>
                  <Flex gap="2">
                    <SelectComponent
                      name="student.eletronicDevice"
                      label="Dispositivo eletrônico "
                      control={control}
                      errors={errors}
                      composedName={{
                        parent: "student",
                        prop: "eletronicDevice",
                      }}
                      options={StudentEletronicDevice?.map(
                        (country: { id: any; description: any }) => ({
                          value: country.id,
                          label: country.description ?? "",
                        })
                      )}
                      style={{ width: "100%" }}
                    />
                    <SelectComponent
                      name="student.specialConditions"
                      label="Necessidades especias"
                      control={control}
                      errors={errors}
                      composedName={{
                        parent: "student",
                        prop: "specialConditions",
                      }}
                      options={StudentSpecialConditions?.map(
                        (country: { id: any; description: any }) => ({
                          value: country.id,
                          label: country.description ?? "",
                        })
                      )}
                      style={{ width: "100%" }}
                    />
                  </Flex>

                  <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                    <Button
                      color="red"
                      disabled={loadingState}
                      onClick={handleBack}
                    >
                      <ChevronLeftIcon /> Sair
                    </Button>

                    <Button
                      disabled={loadingState}
                      onClick={() => {
                        navigateAndValidate("course");
                      }}
                      variant="surface">
                      {false ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                      Proximo
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>
            <Tabs.Content value="course">
              <Card size="1">
                <Flex direction="column">
                  <Flex direction="column" gap="4">
                    <Flex gap="2">
                      <SelectComponent
                        name="student.provinceGraduated12OrEqual"
                        label={provinceEnrolledCompletedLevel}
                        control={control}
                        errors={errors}
                        composedName={{ parent: "student", prop: "province" }}
                        options={provinces?.map(
                          (country: { id: any; description: any }) => ({
                            value: country.id,
                            label: country.description ?? "",
                          })
                        )}
                        style={{ width: "100%" }}
                      />
                      <TextFieldComponent
                        style={{ width: "100%" }}
                        name="student.localGraduated12OrEqual"
                        label="Instituição de conclusão do nível"
                        control={control}
                        errors={errors}
                        fieldProps={{ placeholder: "Instituição de conclusão do nível" }}
                      />
                    </Flex>
                    <SelectComponent
                      name="idUniversity"
                      label="Instituição de Ensino Superior"
                      control={control}
                      errors={errors}
                      style={{ width: "100%" }}
                      options={universities?.items.map((u) => ({
                        value: u.id ?? "",
                        label: u.description ?? "",
                      }))}
                      itemsLoading={isLoadingUniversities}
                      itemsError={errorUniversities}
                      onSelect={handleUniversityChange}
                      required={true}
                    />
                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        <span>
                          Unidade orgânica{" "}
                          <span style={{ color: "red" }}>*</span>
                        </span>
                      </Text>
                      {isLoadingFaculties ? (
                        <Spinner loading />
                      ) : errorFaculties ? (
                        <Text>Erro ao carregar unidades orgânica</Text>
                      ) : (
                        <Controller
                          name="idFaculty"
                          control={control}
                          render={({ field }) => (
                            <Select.Root
                              onValueChange={handleFacultyChange}
                              value={field.value}
                            >
                              <Select.Trigger style={{ width: "100%" }}>
                                {faculties?.items.find(
                                  (faculty) => faculty.id === field.value
                                )?.description ||
                                  "Selecione a unidade orgânica"}
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Group>
                                  <Select.Label>Unidade orgânica</Select.Label>
                                  {faculties?.items.map((faculty) => (
                                    <Select.Item
                                      key={faculty.id}
                                      value={faculty.id ?? ""}
                                    >
                                      {faculty.description}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          )}
                        />
                      )}
                      {errors.idFaculty && (
                        <Text color="red" size="1">
                          {errors.idFaculty.message}
                        </Text>
                      )}
                    </Box>

                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        <span>
                          Departamento <span style={{ color: "red" }}>*</span>
                        </span>
                      </Text>
                      {isLoadingDepartaments ? (
                        <Spinner loading />
                      ) : errorDepartaments ? (
                        <Text>Erro ao carregar departamentos</Text>
                      ) : (
                        <Controller
                          name="idDepartament"
                          control={control}
                          render={({ field }) => (
                            <Select.Root
                              onValueChange={handleDepartamentChange}
                              value={field.value}
                            >
                              <Select.Trigger style={{ width: "100%" }}>
                                {
                                  departaments?.items.find(
                                    (departament) =>
                                      departament.id === field.value
                                  )?.description
                                }
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Group>
                                  <Select.Label>Departamento</Select.Label>
                                  {departaments?.items.map((departament) => (
                                    <Select.Item
                                      key={departament.id}
                                      value={String(departament.id)}
                                    >
                                      {departament.description}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          )}
                        />
                      )}
                      {errors.idDepartament && (
                        <Text color="red" size="1">
                          {errors.idDepartament.message}
                        </Text>
                      )}
                    </Box>

                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        <span>
                          Curso <span style={{ color: "red" }}>*</span>
                        </span>
                      </Text>
                      {isLoadingCourses ? (
                        <Spinner loading />
                      ) : errorCourses ? (
                        <Text>Erro ao carregar cursos</Text>
                      ) : (
                        <Controller
                          name="enrollment.idCourse"
                          control={control}
                          render={({ field }) => (
                            <Select.Root
                              onValueChange={handleCourseChange}
                              value={field.value}
                            >
                              <Select.Trigger style={{ width: "100%" }}>
                                {course?.description}
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Group>
                                  <Select.Label>Curso</Select.Label>
                                  {courses?.items.map((course) => (
                                    <Select.Item
                                      key={course.id}
                                      value={String(course.id)}
                                    >
                                      {course.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          )}
                        />
                      )}
                      {errors.enrollment?.idCourse && (
                        <Text color="red" size="1">
                          {errors.enrollment.idCourse.message}
                        </Text>
                      )}
                    </Box>

                    <Box style={{ width: "100%" }}>
                      <Text as="label" size="2" className={styles.label}>
                        <span>
                          Plano de curso <span style={{ color: "red" }}>*</span>
                        </span>
                      </Text>
                      {isLoadingCoursePlans ? (
                        <Spinner loading />
                      ) : errorCoursePlans ? (
                        <Text>Erro ao carregar cursos</Text>
                      ) : (
                        <Controller
                          name="idPlan"
                          control={control}
                          render={({ field }) => (
                            <Select.Root
                              onValueChange={(value) => field.onChange(value)}
                              value={field.value}
                            >
                              <Select.Trigger style={{ width: "100%" }}>
                                {`${coursePlan?.course} - ${coursePlan?.academicYear}`}
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Group>
                                  <Select.Label>Plano de curso</Select.Label>
                                  {coursePlans?.items.map((coursePlan) => (
                                    <Select.Item
                                      key={coursePlan.id}
                                      value={String(coursePlan.id)}
                                    >
                                      {`${coursePlan.course} - ${coursePlan.academicYear}`}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          )}
                        />
                      )}
                      {errors.idPlan && (
                        <Text color="red" size="1">
                          {errors.idPlan.message}
                        </Text>
                      )}
                    </Box>
                    <Flex gap="8">
                      <TextFieldComponent
                        name="student.enrollmentYear"
                        label="Ano de ingresso"
                        style={{ width: "100%" }}
                        control={control}
                        errors={errors}
                        fieldProps={{ placeholder: "Digite o ano de ingresso" }}
                      />

                      <RadioGroupComponent
                        name="enrollment.subscribeStudent"
                        label="Inscrever estudante"
                        control={control}
                        errors={errors}
                        style={{ width: "100%" }}
                        options={[
                          { value: "true", label: "Sim" },
                          { value: "false", label: "Não" },
                        ]}
                        fieldProps={{
                          style: {
                            display: "flex",
                            flexDirection: "row",
                            gap: "16px",
                          },
                          onChange: handleOnChangeSubscribe,
                        }}
                      />
                    </Flex>
                    <Flex gap="8">
                      <RadioGroupComponent
                        name="isInternStudent"
                        label="Estudante interno"
                        control={control}
                        errors={errors}
                        style={{ width: "100%" }}
                        options={[
                          { value: "true", label: "Sim" },
                          { value: "false", label: "Não" },
                        ]}
                        fieldProps={{
                          style: {
                            display: "flex",
                            flexDirection: "row",
                            gap: "16px",
                          },
                          onChange: handleOnChangeIsInternStudent,
                        }}
                      />
                    </Flex>
                    <Flex gap="4">
                      <TextFieldComponent
                        name="schoolCode"
                        label="Código"
                        style={{ width: "100%" }}
                        control={control}
                        errors={errors}
                        fieldProps={{
                          placeholder: "Digite o código de estudante",
                          disabled: !stringToBoolean(watchIsInternStudent),
                        }}
                      />
                      <TextFieldComponent
                        name="year"
                        label="Ano"
                        style={{ width: "100%" }}
                        control={control}
                        errors={errors}
                        fieldProps={{
                          placeholder: "Digite o ano",
                          disabled: !stringToBoolean(watchIsInternStudent),
                          onChange: handleOnChangeYear,
                        }}
                      />
                      <TextFieldComponent
                        name="semester"
                        label="Semestre"
                        style={{ width: "100%" }}
                        control={control}
                        errors={errors}
                        fieldProps={{
                          placeholder: "Digite o semestre",
                          disabled: !stringToBoolean(watchIsInternStudent),
                        }}
                      />
                    </Flex>
                    <Flex gap="8">
                      <RadioGroupComponent
                        name="enrollment.regime"
                        label="Regime"
                        control={control}
                        errors={errors}
                        style={{ width: "100%" }}
                        options={Regimes.map((r) => ({
                          value: r.id ?? "",
                          label: r.description ?? "",
                        }))}
                        fieldProps={{
                          style: {
                            display: "flex",
                            flexDirection: "row",
                            gap: "16px",
                          },
                        }}
                      />
                    </Flex>
                  </Flex>
                  <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                    <Button
                      color="red"
                      disabled={loadingState}
                      onClick={() => {
                        navigateAndValidate("user");
                      }}
                    >
                      <ChevronLeftIcon /> Voltar
                    </Button>
                    <Button
                      disabled={loadingState}
                      onClick={() => {
                        if (!stringToBoolean(watchSubscribeStudent)) {
                          navigateAndValidate("payments");
                        } else {
                          navigateAndValidate("manage-subjects");
                        }
                      }}
                      variant="surface"
                    >
                      {false ? (
                        <Spinner loading>
                          {" "}
                          <EnterIcon />
                        </Spinner>
                      ) : (
                        <EnterIcon />
                      )}
                      Proximo
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>
            <Tabs.Content
              value="manage-subjects"
              aria-disabled={!stringToBoolean(watchSubscribeStudent)}
            >
              <Card>
                <Flex direction={"column"} gap="4">
                  <CheckboxGroupComponent
                    name="retrySubjectsReproved"
                    label="Disciplinas em atraso"
                    control={control}
                    errors={errors}
                    options={
                      reprovedSubjectsData?.items &&
                      reprovedSubjectsData?.items?.map((reprovedSubjects) => {
                        return {
                          value: JSON.stringify(reprovedSubjects),
                          label: `${reprovedSubjects.subject} - ${reprovedSubjects.semester}º Semestre`,
                        };
                      })
                    }
                    style={{ width: "100%" }}
                    itemsError={isErrorReprovedSubjects}
                    itemsLoading={isLoadingReprovedSubjects}
                  />
                  <CheckboxGroupComponent
                    name="subjectsSubscriptions"
                    label="Disciplinas do semestre"
                    control={control}
                    errors={errors}
                    options={planSubjectsData?.items
                      ?.sort((a, b) => a.level - b.level)
                      .map((item) => {
                        const isAbleToDoEqual =
                          reprovedSubjectsData?.items.find(
                            (rs) => rs.idSubject === item.idSubjectPrecedence
                          );
                        return {
                          value: JSON.stringify({
                            id: item.id,
                            subject: item.subject,
                            semester: item.semester,
                            idSubject: item.idSubject,
                            level: item.level,
                            isMonograph: item?.isMonograph,
                          }),
                          label:
                            `${item.subject} (${item.level}ºAno ${item.semester}ºSemestre)`.concat(
                              (item?.isCore
                                ? isAbleToDoEqual &&
                                isAbleToDoEqual?.id &&
                                " (Nuclear)  Não será inscrito"
                                : isAbleToDoEqual &&
                                isAbleToDoEqual?.id &&
                                " (Opcional)  Não será inscrito") ?? ""
                            ),
                          isCoreBadge: item.isCore ? (
                            <>
                              <Badge color="green">Nuclear</Badge>
                              {isAbleToDoEqual && isAbleToDoEqual?.id && (
                                <Badge color="red">Não será inscrito</Badge>
                              )}
                            </>
                          ) : (
                            <>
                              <Badge color="gray">Opcional</Badge>
                              {isAbleToDoEqual && isAbleToDoEqual?.id && (
                                <Badge color="red">Não será inscrito</Badge>
                              )}
                            </>
                          ),
                        };
                      })}
                    style={{ width: "100%" }}
                    itemsError={isErrorPlanSubjectsData}
                    itemsLoading={isLoadingPlanSubjectsData}
                  />
                  <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                    <Button
                      color="red"
                      disabled={loadingState}
                      onClick={() => {
                        navigateAndValidate("course");
                      }}
                    >
                      <ChevronLeftIcon /> Voltar
                    </Button>
                    <Button
                      disabled={loadingState}
                      onClick={() => {
                        navigateAndValidate("payments");
                      }}
                      variant="surface"
                    >
                      {false ? (
                        <Spinner loading>
                          {" "}
                          <EnterIcon />
                        </Spinner>
                      ) : (
                        <EnterIcon />
                      )}
                      Proximo
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>
            <Tabs.Content value="payments">
              <Flex gap="6" direction="column">
                <Callout.Root>
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Callout.Text>Valor a pagar : {paymentTotal}</Callout.Text>
                </Callout.Root>

                <RadioGroupComponent
                  name="payment.isPaymentByEntityReference"
                  label="Tipo de emolumento"
                  control={control}
                  errors={errors}
                  style={{ width: "50%" }}
                  options={[
                    { value: "true", label: "Entidade Referencia" },
                    { value: "false", label: "Comprovativo" },
                  ]}
                  fieldProps={{
                    style: {
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    },
                    onChange: handleOnChangePaymentType,
                  }}
                />
                <SelectComponent
                  name="payment.idBankEntity"
                  label="Entidade Bancária"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingSchoolschoolBankConfigs}
                  itemsError={isErrorSchoolschoolBankConfigs}
                  options={
                    schoolBankConfigsData?.items?.map((item) => ({
                      value: item.id!,
                      label: String(item.bankEntity!) ?? "",
                    })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{ disabled: !stringToBoolean(watchPaymentType) }}
                />
                <Box>
                  <Text as="label" size="2">
                    Comprovativo de Pagamento
                  </Text>
                  <Controller
                    name={`payment.paymentProof`}
                    control={control}
                    disabled={stringToBoolean(watchPaymentType)}
                    render={({ field }) => (
                      <DragAndDropFileUpload
                        onFileDrop={(file) => {
                          field.onChange(file);
                        }}
                        label={`Arraste ou selecione o arquivo`}
                        accept="*"
                        selectedFile={field.value}
                        isReadOnly={stringToBoolean(watchPaymentType)}
                        disabled={stringToBoolean(watchPaymentType)}
                      />
                    )}
                  />
                  {errors.payment?.paymentProof && (
                    <Text
                      color="red"
                      size="1"
                    >{`${errors.payment.paymentProof.message}`}</Text>
                  )}
                </Box>
                <SelectComponent
                  name="payment.idPaymentTypeEnrollment"
                  label="Valor da matricula"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingPaymentTypes}
                  itemsError={isErrorPaymentTypes}
                  options={
                    paymentTypesData?.items
                      ?.filter((i) => Number(i?.internalCode!) === 25)
                      .map((item) => ({
                        value: item.id!,
                        label: String(item.description!) ?? "",
                      })) ?? []
                  }
                  style={{ width: "100%" }}
                />
                <SelectComponent
                  name="payment.idPaymentTypeSubscription"
                  label="Valor da inscrição"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingPaymentTypes}
                  itemsError={isErrorPaymentTypes}
                  options={
                    paymentTypesData?.items
                      ?.filter((i) => Number(i?.internalCode!) === 26)
                      .map((item) => ({
                        value: item.id!,
                        label: String(item.description!) ?? "",
                      })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{
                    disabled: !stringToBoolean(watchSubscribeStudent),
                  }}
                />
                <SelectComponent
                  name="payment.idPaymentTypePerSubject"
                  label="Valor por disciplina"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingPaymentTypes}
                  itemsError={isErrorPaymentTypes}
                  options={
                    paymentTypesData?.items
                      ?.filter((i) => Number(i?.internalCode!) === 27)
                      .map((item) => ({
                        value: item.id!,
                        label: String(item.description!) ?? "",
                      })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{
                    disabled: !stringToBoolean(watchSubscribeStudent),
                  }}
                />
                <SelectComponent
                  name="payment.idPaymentTypeMonograph"
                  label="Valor da monografia"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingPaymentTypes}
                  itemsError={isErrorPaymentTypes}
                  options={
                    paymentTypesData?.items
                      ?.filter((i) => Number(i?.internalCode!) === 28)
                      .map((item) => ({
                        value: item.id!,
                        label: String(item.description!) ?? "",
                      })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{
                    disabled:
                      !stringToBoolean(watchSubscribeStudent) ||
                      !stringToBoolean(isLastYearOrLevel),
                  }}
                />

                <CheckboxComponent
                  name="isGenerateMonthly"
                  label="Deseja gerar mensalidades?"
                  control={control}
                  errors={errors}
                />
                <SelectComponent
                  name="bankEntity"
                  label="Entidade Bancária"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingSchoolschoolBankConfigs}
                  itemsError={isErrorSchoolschoolBankConfigs}
                  options={
                    schoolBankConfigsData?.items?.map((item) => ({
                      value: item.id!,
                      label: String(item.bankEntity!) ?? "",
                    })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{ disabled: !stringToBoolean(isGenerateMonthly) }}
                />
                <SelectComponent
                  name="paymentTypeId"
                  label="Tipo de emolumento (Emolumento)"
                  control={control}
                  errors={errors}
                  itemsLoading={isLoadingPaymentTypes}
                  itemsError={isErrorPaymentTypes}
                  options={
                    paymentTypesData?.items?.map((item) => ({
                      value: item.id!,
                      label: String(item.description!) ?? "",
                    })) ?? []
                  }
                  style={{ width: "100%" }}
                  fieldProps={{ disabled: !stringToBoolean(isGenerateMonthly) }}
                />

                <Callout.Root>
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Callout.Text>Valor a pagar : {paymentTotal}</Callout.Text>
                </Callout.Root>
                <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                  <Button
                    color="red"
                    disabled={loadingState}
                    onClick={() => {
                      navigateAndValidate("manage-subjects");
                    }}
                  >
                    <ChevronLeftIcon /> Voltar
                  </Button>
                  <Button
                    disabled={loadingState}
                    onClick={() => {
                      navigateAndValidate("documents");
                    }}
                    variant="surface"
                  >
                    {false ? (
                      <Spinner loading>
                        {" "}
                        <EnterIcon />
                      </Spinner>
                    ) : (
                      <EnterIcon />
                    )}
                    Proximo
                  </Button>
                </Flex>
              </Flex>
            </Tabs.Content>
            <Tabs.Content value="documents">
              <Card size="3">
                <Flex direction="column" gap="4">

                  {documentsDataFiltered.length > 0 ? (
                    documentsDataFiltered?.map((requiredDoc, index) => (
                      <Box key={requiredDoc.id} style={{ width: "100%" }}>
                        <Text as="label" size="2" className={styles.label}>
                          <span>
                            {requiredDoc.description}{" "}
                            <span style={{ color: "red" }}>*</span>
                          </span>
                        </Text>
                        <Controller
                          name={`documents.${index}.file` as const}
                          control={control}
                          render={({ field }) => (
                            <DragAndDropFileUpload
                              onFileDrop={(file) => {
                                field.onChange(file);
                                setValue(`documents.${index}.id`, requiredDoc.id);
                              }}
                              label={`Arraste ou selecione o arquivo para ${requiredDoc.description}`}
                              accept="*"
                              selectedFile={field.value}
                              required={true}
                            />
                          )}
                        />
                        {errors.documents?.[index]?.file && (
                          <Text
                            color="red"
                            size="1"
                          >{`${errors.documents[index]?.file?.message} ${requiredDoc.description}`}</Text>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Callout.Root>
                      <Callout.Icon>
                        <InfoCircledIcon />
                      </Callout.Icon>
                      <Callout.Text>
                        Não são necessários documentos adicionais para este curso/regime.
                      </Callout.Text>
                    </Callout.Root>
                  )}

                  <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                    <Button
                      color="red"
                      disabled={loadingState}
                      onClick={() => {
                        navigateAndValidate("payments");
                      }}
                    >
                      <ChevronLeftIcon /> Voltar
                    </Button>
                    <Button
                      color="red"
                      disabled={loadingState}
                      onClick={() => {
                        reset();
                      }}
                    >
                      <ChevronLeftIcon /> Limpar campos
                    </Button>
                    <Button
                      disabled={loadingState}
                      type="submit"
                      variant="surface"
                    >
                      {loadingState ? (
                        <Spinner loading>
                          {" "}
                          <EnterIcon />
                        </Spinner>
                      ) : (
                        <EnterIcon />
                      )}
                      Submeter
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </form>
    </Flex>
  );
};